import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save, TestTube, ArrowLeft, CheckCircle, XCircle, Link as LinkIcon, Key } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { whatsappService } from '../../services/whatsappService';

export default function UniversalApiEndpointConfiguration() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await whatsappService?.getWebhookConfigs();
      setConfigs(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (provider, field, value) => {
    setConfigs(configs?.map(config => 
      config?.provider === provider 
        ? { ...config, [field]: value }
        : config
    ));
  };

  const handleSave = async (provider) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const config = configs?.find(c => c?.provider === provider);
      await whatsappService?.updateWebhookConfig(provider, {
        webhookUrl: config?.webhookUrl,
        apiKey: config?.apiKey,
        isActive: config?.isActive
      });
      
      setSuccess(`Configuration ${provider} enregistrée avec succès`);
      await loadConfigs();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    try {
      setTesting({ ...testing, [provider]: true });
      setTestResults({ ...testResults, [provider]: null });
      
      const result = await whatsappService?.testWebhook(provider);
      setTestResults({
        ...testResults,
        [provider]: result
      });
    } catch (err) {
      setTestResults({
        ...testResults,
        [provider]: { success: false, error: err?.message }
      });
    } finally {
      setTesting({ ...testing, [provider]: false });
    }
  };

  const getProviderInfo = (provider) => {
    const info = {
      make: {
        name: 'Make.com',
        description: 'Automatisation avancée avec scénarios personnalisés',
        color: 'purple',
        docs: 'https://www.make.com/en/help/webhooks'
      },
      manychat: {
        name: 'ManyChat',
        description: 'Chatbot WhatsApp avec templates prédéfinis',
        color: 'blue',
        docs: 'https://manychat.com/blog/webhooks-api'
      },
      n8n: {
        name: 'n8n',
        description: 'Workflow automation open-source et flexible',
        color: 'orange',
        docs: 'https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.webhook/'
      },
      whatsapp_cloud: {
        name: 'WhatsApp Cloud API',
        description: 'API officielle WhatsApp Business',
        color: 'green',
        docs: 'https://developers.facebook.com/docs/whatsapp/cloud-api'
      }
    };
    return info?.[provider] || { name: provider, description: '', color: 'gray', docs: '#' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des configurations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/notifications')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour aux notifications
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Settings className="w-8 h-8 mr-3 text-blue-600" />
                Configuration des API WhatsApp
              </h1>
              <p className="mt-2 text-gray-600">
                Configurez les webhooks pour chaque provider de notifications WhatsApp
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        {/* Configuration Cards */}
        <div className="space-y-6">
          {configs?.map((config) => {
            const providerInfo = getProviderInfo(config?.provider);
            const testResult = testResults?.[config?.provider];
            
            return (
              <div key={config?.provider} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className={`bg-${providerInfo?.color}-50 border-b border-${providerInfo?.color}-100 px-6 py-4`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {providerInfo?.name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {providerInfo?.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={config?.isActive}
                            onChange={(e) => handleConfigChange(config?.provider, 'isActive', e?.target?.checked)}
                            className="sr-only"
                          />
                          <div className={`block w-14 h-8 rounded-full ${config?.isActive ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${config?.isActive ? 'transform translate-x-6' : ''}`}></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {config?.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                {/* Body */}
                <div className="px-6 py-6 space-y-4">
                  {/* Webhook URL */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Webhook URL
                    </label>
                    <Input
                      type="url"
                      value={config?.webhookUrl || ''}
                      onChange={(e) => handleConfigChange(config?.provider, 'webhookUrl', e?.target?.value)}
                      placeholder={`https://hook.${config?.provider}.com/your-webhook-id`}
                      className="w-full"
                    />
                  </div>

                  {/* API Key */}
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Key className="w-4 h-4 mr-2" />
                      API Key / Token (optionnel)
                    </label>
                    <Input
                      type="password"
                      value={config?.apiKey || ''}
                      onChange={(e) => handleConfigChange(config?.provider, 'apiKey', e?.target?.value)}
                      placeholder="Votre clé API ou token d'authentification"
                      className="w-full"
                    />
                  </div>

                  {/* Documentation Link */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      📚 <strong>Documentation:</strong>{' '}
                      <a 
                        href={providerInfo?.docs} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-blue-900"
                      >
                        Guide d'intégration {providerInfo?.name}
                      </a>
                    </p>
                  </div>

                  {/* Test Result */}
                  {testResult && (
                    <div className={`border rounded-lg p-4 ${testResult?.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center">
                        {testResult?.success ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-green-800 font-medium">
                              Test réussi - Connexion établie
                            </span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5 text-red-600 mr-2" />
                            <span className="text-red-800 font-medium">
                              Test échoué: {testResult?.error || testResult?.statusText}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={() => handleSave(config?.provider)}
                      disabled={saving}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    <Button
                      onClick={() => handleTest(config?.provider)}
                      disabled={testing?.[config?.provider] || !config?.webhookUrl}
                      variant="outline"
                      className="flex-1"
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      {testing?.[config?.provider] ? 'Test en cours...' : 'Tester la connexion'}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Integration Guide */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Guide d'intégration
          </h3>
          
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">1. Format JSON envoyé</h4>
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
{`{
  "phone": "+33600000000",
  "message": "Votre message ici",
  "shipment_id": "uuid",
  "provider": "n8n",
  "timestamp": "2025-01-19T23:46:31Z",
  "webhook_source": "ADT Tracking"
}`}
              </pre>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">2. Configuration recommandée</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Activer uniquement le provider principal (n8n recommandé)</li>
                <li>Tester la connexion avant activation</li>
                <li>Sécuriser avec API Key si possible</li>
                <li>Vérifier les logs dans /admin/notifications</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">3. Fallback automatique</h4>
              <p>
                Si aucun webhook n'est configuré, le système enregistre les notifications 
                en mode "mock" (statut: envoyé) pour préserver l'historique.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}